<template>
  <w-sliding-popup ref="slidingPopup" title="필터 설정" width="500px" @close="close">
    <div slot="content">
      <el-row class="title-h4 title-bar vertical-center-box" type="flex" justify="space-between">
        <div>
          <span class="mr-r15">그룹 목록</span>
          <el-button type="primary" @click="openFilterGroupSettingPopup">그룹추가</el-button>
          <el-button :disabled="checkedGroup.length === 0" @click="onDeleteGroup">그룹삭제</el-button>
        </div>
        <!-- 드래그해서 필터그룹 순서 바뀔 경우 사용 (그전에는 비활성화) -->
        <div align="right">
          <el-button type="secondary" size="mini" :disabled="isEmpty(filterGroups)" v-show="!isChangeGroupOrder" @click="onToggleGroupOrderChange">순서변경</el-button>
          <el-button type="primary" size="mini" v-show="isChangeGroupOrder" @click="onUpdateGroupOrder">저장</el-button>
          <el-button size="mini" v-show="isChangeGroupOrder" @click="onCancelChangeGroupOrder">취소</el-button>
        </div>
      </el-row>
      <div v-show="!isChangeGroupOrder">
        <el-table-wrapper class="mr-b30" ref="groupTable" :data="filterGroups" :columns="groupColumn" :pagination="false" :default-column="columnDefault" highlight-current-row max-height="300"
          @selection-change="onGroupSelectionChange" @row-click="onClickGroup">
          <template slot-scope="scope" slot="edit-filter-group">
            <el-button size="mini" @click="openFilterGroupUpdatePopup(scope.row)">수정</el-button>
          </template>
        </el-table-wrapper>
      </div>
      <div v-show="isChangeGroupOrder">
        <draggable v-model="filterGroups" class="list-drag-box mr-b30" element="ul" @start="isDraggable=true" @end="isDraggable=false">
           <li v-for="element in filterGroups" :key="element.filterGroupId">
             <div class="label">{{element.filterGroupName}}</div>
           </li>
        </draggable>
      </div>
      <hr class="mr-b30" />
      <el-row class="title-h4 title-bar vertical-center-box" type="flex" justify="space-between">
        <div>
          <span class="mr-r15">필터 목록</span>
          <el-button type="primary" :disabled="isEmpty(selectedGroup)" @click="openFilterAddPopup">필터추가</el-button>
          <el-button :disabled="checkedFilter.length === 0" @click="onDeleteFilter">필터삭제</el-button>
        </div>
        <!-- 드래그해서 필터 순서 바뀔 경우 사용 -->
        <div align="right">
          <el-button type="secondary" size="mini" :disabled="isEmpty(filters)" v-show="!isChangeFilterOrder" @click="onToggleFilterOrderChange">순서변경</el-button>
          <el-button type="primary" size="mini" v-show="isChangeFilterOrder" @click="onUpdateFilterOrder">저장</el-button>
          <el-button size="mini" v-show="isChangeFilterOrder" @click="onCancelChangeFilterOrder">취소</el-button>
        </div>
      </el-row>
      <div v-show="!isChangeFilterOrder">
        <el-table-wrapper class="mr-b30" :data="filters" :columns="filterColumn" :pagination="false" :default-column="columnDefault" @selection-change="onFilterSelectionChange">
          <template slot-scope="scope" slot="edit-filter">
            <el-button size="mini" @click="openFilterUpdatePopup(scope.row)">수정</el-button>
          </template>
        </el-table-wrapper>
      </div>
      <div v-show="isChangeFilterOrder">
        <draggable v-model="filters" class="list-drag-box" element="ul" @start="isDraggable=true" @end="isDraggable=false">
           <li v-for="element in filters" :key="element.filterId">
             <div class="label">{{element.filterName}}</div>
           </li>
        </draggable>
      </div>
      <w-filter-add-popup :filter-group="selectedGroup" @reload-tree-data="reloadTreeData" ref="filterAddPopup" />
      <w-filter-update-popup @reload-tree-data="reloadTreeData" ref="filterUpdatePopup" />
      <w-filter-group-setting-popup ref="filterGroupSetting" :orderby-index="filterGroupOrderbyIndex" @reload-filter-group="reloadFilterGroup"/>
    </div>
  </w-sliding-popup>
</template>

<script>
import draggable from 'vuedraggable';
import { cloneDeep, isEmpty, differenceWith, isEqual } from 'lodash';

import LogAPI from '@/api/log/integrated-log';
import { columnDefault } from '@/constants/table';
import msgUtils from '@/utils/message';

import WFilterAddPopup from './LogFilterAddPopup';
import WFilterUpdatePopup from './LogFilterUpdatePopup';
import WFilterGroupSettingPopup from './FilterGroupSettingPopup';

export default {
  name: 'WLogFilterSettingPopup',
  components: {
    draggable,
    WFilterAddPopup,
    WFilterUpdatePopup,
    WFilterGroupSettingPopup,
  },
  data() {
    const groupColumn = [
      {
        type: 'selection',
        width: '60',
      },
      {
        label: '그룹명',
        prop: 'filterGroupName',
      },
      {
        label: '수정',
        scopedSlot: 'edit-filter-group',
        width: '70',
      },
    ];
    const filterColumn = [
      {
        type: 'selection',
        width: '60',
      },
      {
        label: '필터명',
        prop: 'filterName',
      },
      {
        label: '수정',
        scopedSlot: 'edit-filter',
        width: '70',
      },
    ];
    return {
      columnDefault,
      groupColumn,
      filterColumn,
      filterGroups: [],
      filterGroupsOrigin: [],
      filters: [],
      filtersOrigin: [],
      filterGroupOrderbyIndex: 0,
      isDraggable: false,
      checkedGroup: [],
      selectedGroup: {},
      checkedFilter: [],
      isChangeGroupOrder: false,
      isChangeFilterOrder: false,
    };
  },
  watch: {
    selectedGroup: {
      deep: true,
      handler(value) {
        if (!isEmpty(value)) {
          this.loadFilterData();
        }
      },
    },
    filterGroups(value) {
      if (value.length > 0) {
        value.forEach((group, index) => {
          group.orderbyIndex = index + 1;
        });
      }
    },
    filters(value) {
      if (value.length > 0) {
        value.forEach((filter, index) => {
          filter.orderbyIndex = index + 1;
        });
      }
    },
  },
  methods: {
    // TODO 그룹 이름 변경 후 저장 시 API 호출하는 함수 필요
    /**
     * 로그 필터 그룹 목록을 조회해온다.
     * @author Minyeong
     * @return {[type]} [description]
     */
    loadData() {
      LogAPI.getLogFilterGroups().then((response) => {
        if (response.data.success) {
          this.filterGroups = cloneDeep(response.data.data);
          this.filterGroupsOrigin = cloneDeep(response.data.data);
          this.$nextTick(() => {
            if (!isEmpty(this.$refs.groupTable) && !isEmpty(this.$refs.groupTable.$children) && !isEmpty(this.filterGroups[0])) {
              this.$refs.groupTable.$children[0].setCurrentRow(this.filterGroups[0]);
              this.selectedGroup = this.filterGroups[0];
            }
          });
        }
      });
    },
    /**
     * 선택한 로그 필터 그룹의 하위 필터 목록을 조회해온다.
     * @author Minyeong
     * @return {[type]} [description]
     */
    loadFilterData() {
      const params = {
        params: {
          filterGroupId: this.selectedGroup.filterGroupId,
        },
      };
      LogAPI.getLogFiltersByGroup(params).then((response) => {
        if (response.data.success) {
          this.filters = cloneDeep(response.data.data);
          this.filtersOrigin = cloneDeep(response.data.data);
        }
      });
    },
    reloadTreeData() {
      this.$emit('reload-tree-data');
      this.loadFilterData();
    },
    reloadFilterGroup() {
      this.$emit('reload-tree-data');
      this.loadData();
    },
    onClickGroup(row) {
      this.selectedGroup = row;
    },
    onUpdateGroupOrder() {
      const params = differenceWith(this.filterGroups, this.filterGroupsOrigin, isEqual);

      LogAPI.updateFilterGroupOrder(params).then((response) => {
        this.$message(msgUtils.getResultMsgData(msgUtils.action.SAVE, '필터 그룹', response.data.success));
        if (response.data.success) {
          this.loadData();
          this.$emit('reload-tree-data');
          this.onToggleGroupOrderChange();
        }
      });
    },
    onUpdateFilterOrder() {
      const params = differenceWith(this.filters, this.filtersOrigin, isEqual);

      LogAPI.updateFilterOrder(params).then((response) => {
        this.$message(msgUtils.getResultMsgData(msgUtils.action.SAVE, '필터', response.data.success));
        if (response.data.success) {
          this.loadData();
          this.$emit('reload-tree-data');
          this.onToggleFilterOrderChange();
        }
      });
    },
    onDeleteGroup() {
      this.$confirm(...msgUtils.getBasicConfirmData(msgUtils.action.DELETE)).then(() => {
        if (!isEmpty(this.checkedGroup)) {
          LogAPI.deleteFilterGroup(this.checkedGroup).then((response) => {
            this.$message(msgUtils.getResultMsgData(msgUtils.action.DELETE, '필터 그룹', response.data.success));
            if (response.data.success) {
              this.loadData();
              this.$emit('reload-tree-data');
            }
          });
        }
      }).catch(() => {});
    },
    onDeleteFilter() {
      this.$confirm(...msgUtils.getBasicConfirmData(msgUtils.action.DELETE)).then(() => {
        LogAPI.deleteFilter(this.checkedFilter).then((response) => {
          this.$message(msgUtils.getResultMsgData(msgUtils.action.DELETE, '필터', response.data.success));
          if (response.data.success) {
            this.loadFilterData();
            this.$emit('reload-tree-data');
          }
        });
      }).catch(() => {});
    },
    onGroupSelectionChange(selection) {
      this.checkedGroup = selection;
    },
    onFilterSelectionChange(selection) {
      this.checkedFilter = selection;
    },
    onToggleGroupOrderChange() {
      this.isChangeGroupOrder = !this.isChangeGroupOrder;
    },
    onToggleFilterOrderChange() {
      this.isChangeFilterOrder = !this.isChangeFilterOrder;
    },
    onCancelChangeGroupOrder() {
      this.isChangeGroupOrder = false;
      this.filterGroups = cloneDeep(this.filterGroupsOrigin);
    },
    onCancelChangeFilterOrder() {
      this.isChangeFilterOrder = false;
      this.filters = cloneDeep(this.filtersOrigin);
    },
    openFilterAddPopup() {
      let orderbyIndex = 1;
      if (!isEmpty(this.filters)) {
        orderbyIndex = this.filters[this.filters.length - 1].orderbyIndex;
      }
      this.$refs.filterAddPopup.open(orderbyIndex);
    },
    openFilterUpdatePopup(filterInfo) {
      this.$refs.filterUpdatePopup.open(filterInfo);
    },
    openFilterGroupSettingPopup() {
      if (isEmpty(this.filterGroups)) {
        this.filterGroupOrderbyIndex = 0;
      } else {
        this.filterGroupOrderbyIndex = this.filterGroups[this.filterGroups.length - 1].orderbyIndex + 1;
      }
      this.$refs.filterGroupSetting.open();
    },
    openFilterGroupUpdatePopup(filterGroupInfo) {
      this.$refs.filterGroupSetting.open(filterGroupInfo);
    },
    open() {
      this.isChangeGroupOrder = false;
      this.isChangeFilterOrder = false;
      this.$refs.slidingPopup.open();
      this.loadData();
    },
    close() {
      // 수정 중이던 값들 모두 초기화 >> 나중에 API에서 리로딩하는 방식으로 변경
      this.dummy = this.dummyClone;
    },
    isEmpty,
  },
};
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/views/log-analysis/popup/LogFilterSettingPopup.vue